import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import { auth } from '@/api/openapi/middleware';
import { paths } from '@/api/openapi/schema';
import { Slide } from '@/components/slides/Slide';
import { CANVAS_SIZE } from '@/constant';
import { capture } from '@/handlers/error';
import { Lesson } from '@/models/lesson';
import { useSession } from '@/providers/session/SessionProvider';
import { SlideComponent } from '@/types/slide';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Divider, FormControl, InputLabel, ListSubheader, MenuItem, Select, Skeleton, Typography } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import createClient from 'openapi-fetch';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generate as generateFillInTheBlankComponents } from './generators/demo/fitb';
import { generate as generateMatchingComponents } from './generators/demo/matching';
import { generate as generateGcseRcMcComponents } from './generators/gcse/rc';
import { generate as generateMCComponents } from './generators/igcse/rc';
import { generate as generateSpeakingComponents } from './generators/igcse/speaking';
import { FillInTheBlankContext, FillInTheBlankPanel } from './panels/demo/FillInTheBlankPanel';
import { MatchingContext, MatchingPanel } from './panels/demo/MatchingPanel';
import { GcseRcMcContext, RCPanel as GcseRCPanel } from './panels/gcse/RCPanel';
import { ArticleType, RCPanel, ReadingComprehensionContext } from './panels/igcse/RCPanel';
import { SpeakingContext, SpeakingPanel, SpeakingType } from './panels/igcse/SpeakingPanel';

// Track types
type Track = 'gcse' | 'igcse' | 'general';
type ContextType = MatchingContext | FillInTheBlankContext | ReadingComprehensionContext | SpeakingContext | GcseRcMcContext;

// Difficulty level options by track
const TRACK_OPTIONS: Track[] = ['gcse', 'igcse', 'general'];

const DIFFICULTY_LEVELS_BY_TRACK: Record<Track, { value: string; label: string; isCategory?: boolean }[]> = {
    gcse: [{ value: 'higher', label: 'Higher Tier' }],
    igcse: [{ value: 'foreign_language_0547', label: 'Foreign Language (0547)' }],
    general: [
        // Kindergarten category
        { value: 'category-kindergarten', label: 'experiment.generator.question.grade.category.kindergarten', isCategory: true },
        { value: '幼稚園 K1', label: 'experiment.generator.question.grade.level.1' },
        { value: '幼稚園 K2', label: 'experiment.generator.question.grade.level.2' },
        { value: '幼稚園 K3', label: 'experiment.generator.question.grade.level.3' },
        // Primary category
        { value: 'category-primary', label: 'experiment.generator.question.grade.category.primary', isCategory: true },
        { value: '小學一年級', label: 'experiment.generator.question.grade.level.4' },
        { value: '小學二年級', label: 'experiment.generator.question.grade.level.5' },
        { value: '小學三年級', label: 'experiment.generator.question.grade.level.6' },
        { value: '小學四年級', label: 'experiment.generator.question.grade.level.7' },
        { value: '小學五年級', label: 'experiment.generator.question.grade.level.8' },
        { value: '小學六年級', label: 'experiment.generator.question.grade.level.9' },
        // Secondary category
        { value: 'category-secondary', label: 'experiment.generator.question.grade.category.secondary', isCategory: true },
        { value: '中學一級', label: 'experiment.generator.question.grade.level.10' },
        { value: '中學二級', label: 'experiment.generator.question.grade.level.11' },
        { value: '中學三級', label: 'experiment.generator.question.grade.level.12' },
        { value: '中學四級', label: 'experiment.generator.question.grade.level.13' },
        { value: '中學五級', label: 'experiment.generator.question.grade.level.14' },
        { value: '中學六級', label: 'experiment.generator.question.grade.level.15' },
    ],
};

// Supported question types by track
type QuestionType = 'matching' | 'fill_in_the_blank' | 'readingComprehension' | 'speaking' | 'gcseRcMc';

const SUPPORTED_QUESTION_TYPES_BY_TRACK: Record<Track, { value: QuestionType; label: string }[]> = {
    gcse: [{ value: 'gcseRcMc', label: 'experiment.generator.exercise.type.readingComprehension' }],
    igcse: [
        { value: 'readingComprehension', label: 'experiment.generator.exercise.type.readingComprehension' },
        { value: 'speaking', label: 'experiment.generator.exercise.type.speaking' },
    ],
    general: [
        { value: 'fill_in_the_blank', label: 'experiment.generator.question.type.fillInTheBlank' },
        { value: 'matching', label: 'experiment.generator.question.type.matching' },
    ],
};

// Helper function to call the correct RC endpoint
const callRCEndpoint = async (
    client: ReturnType<typeof createClient<paths>>,
    articleType: ArticleType,
    requestBody: {
        context: {
            type: 'mc';
            topic: string;
            subtopic?: string;
            language_variant?: 'traditional' | 'simplified';
            question_count?: number;
        };
    },
) => {
    switch (articleType) {
        case 'narrative':
            return client.POST('/experiments/generators/questions/igcse/rc/narrative', { body: requestBody });
        case 'letter':
            return client.POST('/experiments/generators/questions/igcse/rc/letter', { body: requestBody });
        case 'review':
            return client.POST('/experiments/generators/questions/igcse/rc/review', { body: requestBody });
        default:
            throw new Error(`Unsupported article type: ${articleType}`);
    }
};

// Helper function to call the correct speaking endpoint
const callSpeakingEndpoint = async (
    client: ReturnType<typeof createClient<paths>>,
    articleType: SpeakingType,
    requestBody: {
        context: {
            type: 'recording';
            topic: string;
            subtopic?: string;
            language_variant?: 'traditional' | 'simplified';
            question_count?: number;
        };
    },
) => {
    switch (articleType) {
        case 'rolePlay':
            return client.POST('/experiments/generators/questions/igcse/speaking/role-play', { body: requestBody });
        case 'topicConversation':
            return client.POST('/experiments/generators/questions/igcse/speaking/topic-conversation', { body: requestBody });
        default:
            throw new Error(`Unsupported article type: ${articleType}`);
    }
};

export const SmartSlideGeneratorDialog: FC<
    {
        onCancel?: () => void;
        onSave?: (components: SlideComponent[]) => Promise<void>;
        lesson?: Lesson;
    } & DialogProps
> = ({ onCancel, onSave, lesson, ...props }) => {
    // provider
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { session } = useSession(); // Assuming you have a session provider to get the current session

    // ui state
    const [saving, setSaving] = useState(false);
    const [slides, setSlides] = useState<SlideComponent[][]>([]);
    const [generating, setGenerating] = useState(false);
    const [context, setContext] = useState<ContextType>({ type: 'gcseRcMc' });
    const [track, setTrack] = useState<Track>('gcse');
    const [difficultyLevel, setDifficultyLevel] = useState<string>(DIFFICULTY_LEVELS_BY_TRACK['gcse'][0]?.value ?? 'foundation');

    const handleMatchingGeneration = useCallback(
        async (context: MatchingContext) => {
            try {
                setGenerating(true);

                // Create the API client and send the assessment request
                const client = createClient<paths>({ baseUrl: `${import.meta.env.VITE_API_SERVICE_URL}/v1` });
                client.use(auth(session?.token));

                const { data, error } = await client.POST('/experiments/generators/questions/demo/matching', {
                    body: {
                        level: difficultyLevel,
                        context: {
                            type: 'matching',
                            themes: context.themes ?? [],
                            generation: {
                                image: context.generation?.image,
                            },
                        },
                        output: {
                            locale: context.locale,
                            prefix: lesson ? `lessons/${lesson.id}` : `lessons/temporary`,
                        },
                    },
                });

                if (error) {
                    throw error.error;
                }

                if (data.question.type === 'none') {
                    const message = data.message || 'Failed to generate question. Please contact our support team.';
                    notifications.show(message, { severity: 'warning', autoHideDuration: 7500 });
                    console.warn('Failed to generate question:', data.message);
                    return;
                }

                const slides = await generateMatchingComponents(data);
                setSlides(slides);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setGenerating(false);
            }
        },
        [difficultyLevel, lesson, notifications, session?.token],
    );

    const handleFillIntheBlankGeneration = useCallback(
        async (context: FillInTheBlankContext) => {
            try {
                setGenerating(true);

                // Create the API client and send the assessment request
                const client = createClient<paths>({ baseUrl: `${import.meta.env.VITE_API_SERVICE_URL}/v1` });
                client.use(auth(session?.token));

                const { data, error } = await client.POST('/experiments/generators/questions/demo/fitb', {
                    body: {
                        level: difficultyLevel,
                        context: {
                            type: 'fill_in_the_blank',
                            answer: context.answer || '',
                        },
                        output: {
                            locale: context.locale,
                        },
                    },
                });

                if (error) {
                    throw error.error;
                }

                if (data.question.type === 'none') {
                    const message = data.message || 'Failed to generate question. Please contact our support team.';
                    notifications.show(message, { severity: 'warning', autoHideDuration: 7500 });
                    console.warn('Failed to generate question:', data.message);
                    return;
                }

                const slides = await generateFillInTheBlankComponents(data);
                setSlides(slides);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setGenerating(false);
            }
        },
        [difficultyLevel, notifications, session?.token],
    );

    const handleReadingComprehensionGeneration = useCallback(
        async (context: ReadingComprehensionContext) => {
            try {
                setGenerating(true);

                // Create the API client and send the assessment request
                const client = createClient<paths>({ baseUrl: `${import.meta.env.VITE_API_SERVICE_URL}/v1` });
                client.use(auth(session?.token));

                // Determine the correct endpoint based on article type
                const articleType = context.article_type || 'narrative';

                const requestBody = {
                    context: {
                        type: 'mc' as const,
                        topic: context.topic || '',
                        subtopic: context.subtopic || '',
                        language_variant: context.language_variant || undefined,
                        question_count: context.question_count || 2,
                    },
                };

                // Call the appropriate endpoint based on article type
                const { data, error } = await callRCEndpoint(client, articleType as ArticleType, requestBody);

                if (error) {
                    throw error.error;
                }

                if (data && data.question.type === 'none') {
                    const message = data.message || 'Failed to generate question. Please contact our support team.';
                    notifications.show(message, { severity: 'warning', autoHideDuration: 7500 });
                    console.warn('Failed to generate question:', data.message);
                    return;
                }

                const slides = generateMCComponents(data);
                setSlides(slides);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setGenerating(false);
            }
        },
        [notifications, session?.token],
    );

    const handleSpeakingGeneration = useCallback(
        async (context: SpeakingContext) => {
            try {
                setGenerating(true);

                // Create the API client and send the assessment request
                const client = createClient<paths>({ baseUrl: `${import.meta.env.VITE_API_SERVICE_URL}/v1` });
                client.use(auth(session?.token));

                // Determine the correct endpoint based on speaking type
                const speakingType = context.speaking_type || 'rolePlay';

                const requestBody = {
                    context: {
                        type: 'recording' as const,
                        topic: context.topic || '',
                        subtopic: context.subtopic || '',
                        language_variant: context.language_variant || undefined,
                        question_count: context.question_count || 2,
                    },
                };

                // Call the appropriate endpoint based on speaking type
                const { data, error } = await callSpeakingEndpoint(client, speakingType as SpeakingType, requestBody);

                if (error) {
                    throw error.error;
                }

                if (data && data.question.type === 'none') {
                    const message = data.message || 'Failed to generate question. Please contact our support team.';
                    notifications.show(message, { severity: 'warning', autoHideDuration: 7500 });
                    console.warn('Failed to generate question:', data.message);
                    return;
                }

                const slides = generateSpeakingComponents(data);
                setSlides(slides);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setGenerating(false);
            }
        },
        [notifications, session?.token],
    );

    const handleGcseRcMcGeneration = useCallback(
        async (context: GcseRcMcContext) => {
            try {
                setGenerating(true);

                // Create the API client and send the assessment request
                const client = createClient<paths>({ baseUrl: `${import.meta.env.VITE_API_SERVICE_URL}/v1` });
                client.use(auth(session?.token));

                const { data, error } = await client.POST('/experiments/generators/questions/gcse/rc', {
                    body: {
                        context: {
                            type: 'mc' as const,
                            theme: context.theme || '',
                            topic: context.topic || '',
                            article_type: context.article_type || 'advertisement',
                            language_variant: context.language_variant || 'traditional',
                            question_count: context.question_count || 1,
                        },
                    },
                });

                if (error) {
                    throw error.error;
                }

                if (data && data.question.type === 'none') {
                    const message = data.message || 'Failed to generate question. Please contact our support team.';
                    notifications.show(message, { severity: 'warning', autoHideDuration: 7500 });
                    console.warn('Failed to generate question:', data.message);
                    return;
                }

                const slides = generateGcseRcMcComponents(data);
                setSlides(slides);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setGenerating(false);
            }
        },
        [notifications, session?.token],
    );

    return (
        <Dialog fullWidth maxWidth='lg' {...props}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {t('experiment.generator.question.title')}
                <AutoAwesomeIcon color='primary' />
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                    <Box sx={{ width: 280 }}>
                        <Box
                            component='fieldset'
                            sx={{
                                borderRadius: 1,
                                borderColor: 'divider',
                                borderWidth: 1,
                                p: 2,
                                pt: 1,
                                borderStyle: 'solid',
                            }}
                        >
                            <Typography component='legend' sx={{ px: 1, mb: 2 }}>
                                {t('experiment.generator.question.curriculum.title')}
                            </Typography>

                            <FormControl fullWidth>
                                <InputLabel>{t('experiment.generator.question.curriculum.track')}</InputLabel>
                                <Select
                                    label={t('experiment.generator.question.curriculum.track')}
                                    value={track}
                                    onChange={(e) => {
                                        const value = e.target.value as Track;
                                        setTrack(value);

                                        // Reset difficulty level to the first option of the new track
                                        const level = DIFFICULTY_LEVELS_BY_TRACK[value].filter((v) => !v.isCategory)[0]?.value ?? 'foundation';
                                        setDifficultyLevel(level);

                                        // Reset context type to the first supported question type of the new track
                                        const supportedTypes = SUPPORTED_QUESTION_TYPES_BY_TRACK[value];
                                        if (supportedTypes[0]) {
                                            setContext({ type: supportedTypes[0]!.value });
                                        }
                                    }}
                                    fullWidth
                                >
                                    {TRACK_OPTIONS.map((trackOption) => (
                                        <MenuItem key={trackOption} value={trackOption}>
                                            {t(`experiment.generator.question.curriculum.options.${trackOption}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Divider sx={{ mt: 2, mb: 2 }} />

                            <FormControl fullWidth>
                                <InputLabel>{t('experiment.generator.question.curriculum.difficultyLevel')}</InputLabel>
                                <Select
                                    label={t('experiment.generator.question.curriculum.difficultyLevel')}
                                    value={difficultyLevel}
                                    onChange={(e) => {
                                        setDifficultyLevel(e.target.value as string);
                                    }}
                                    fullWidth
                                >
                                    {DIFFICULTY_LEVELS_BY_TRACK[track].map((option) =>
                                        'isCategory' in option && option.isCategory ? (
                                            <ListSubheader sx={{ color: (theme) => theme.palette.secondary.main }}>{t(option.label)}</ListSubheader>
                                        ) : (
                                            <MenuItem key={option.value} value={option.value}>
                                                {t(option.label)}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box
                            component='fieldset'
                            sx={{
                                borderRadius: 1,
                                borderColor: 'divider',
                                borderWidth: 1,
                                p: 2,
                                pt: 1,
                                borderStyle: 'solid',
                                my: 1.5,
                            }}
                        >
                            <Typography component='legend' sx={{ px: 1, mb: 2 }}>
                                {t('common.settings')}
                            </Typography>

                            <FormControl fullWidth>
                                <InputLabel>{t('experiment.generator.exercise.label.exerciseType')}</InputLabel>
                                <Select
                                    label={t('experiment.generator.exercise.label.exerciseType')}
                                    value={context.type}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value !== 'matching' && value !== 'fill_in_the_blank' && value !== 'readingComprehension' && value !== 'speaking' && value !== 'gcseRcMc') {
                                            notifications.show(t('Unknown question type'), { severity: 'error' });
                                            return;
                                        }
                                        setContext({ type: value });
                                    }}
                                    fullWidth
                                >
                                    {SUPPORTED_QUESTION_TYPES_BY_TRACK[track].map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {t(type.label)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Divider sx={{ mt: 2, mb: 2 }} />

                            {context.type === 'gcseRcMc' && <GcseRCPanel value={context} onChange={(value) => setContext(value)} />}
                            {context.type === 'matching' && <MatchingPanel value={context} onChange={(value) => setContext(value)} />}
                            {context.type === 'fill_in_the_blank' && <FillInTheBlankPanel value={context} onChange={(value) => setContext(value)} />}
                            {context.type === 'readingComprehension' && <RCPanel value={context} onChange={(value) => setContext(value)} />}
                            {context.type === 'speaking' && <SpeakingPanel value={context} onChange={(value) => setContext(value)} />}
                        </Box>
                        <Button
                            variant='contained'
                            fullWidth
                            startIcon={<AutoFixHighIcon />}
                            onClick={() => {
                                if (context === undefined) {
                                    notifications.show('Missing context', { severity: 'error' });
                                    return;
                                }

                                if (!context.isSufficient || !context.isSufficient()) {
                                    notifications.show('Context is not enough', { severity: 'error' });
                                    return;
                                }

                                // clean up previous results
                                setSlides([]);

                                switch (context.type) {
                                    case 'matching':
                                        handleMatchingGeneration(context);
                                        break;
                                    case 'fill_in_the_blank':
                                        handleFillIntheBlankGeneration(context);
                                        break;
                                    case 'readingComprehension':
                                        handleReadingComprehensionGeneration(context);
                                        break;
                                    case 'speaking':
                                        handleSpeakingGeneration(context);
                                        break;
                                    case 'gcseRcMc':
                                        handleGcseRcMcGeneration(context);
                                        break;
                                }
                            }}
                            loading={generating}
                            disabled={context?.isSufficient ? !context.isSufficient() : true}
                        >
                            {t('experiment.generator.question.generate')}
                        </Button>
                    </Box>
                    <Box
                        sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 400,
                            minHeight: 300,
                            flex: 1,
                            aspectRatio: 4 / 3,
                            backgroundColor: (theme.vars || theme).palette.grey[300],
                            ...theme.applyStyles('dark', {
                                backgroundColor: (theme.vars || theme).palette.grey[700],
                            }),
                        })}
                    >
                        {generating ? (
                            <Skeleton variant='rectangular' width={'100%'} height={'100%'} sx={{ bgcolor: 'white' }} />
                        ) : (
                            <Box
                                sx={{
                                    maxWidth: CANVAS_SIZE.width,
                                    maxHeight: CANVAS_SIZE.height,
                                    width: '100%',
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'auto',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    alignContent: 'center',
                                }}
                            >
                                {slides.map((components, index) => (
                                    <>
                                        <Box
                                            sx={{
                                                maxWidth: CANVAS_SIZE.width,
                                                maxHeight: CANVAS_SIZE.height,
                                                width: '100%',
                                                aspectRatio: CANVAS_SIZE.width / CANVAS_SIZE.height,
                                                position: 'relative',
                                            }}
                                        >
                                            <Slide components={components} preview={{ answer: true }} />
                                        </Box>
                                        {slides.length > 1 && index < slides.length - 1 && <Divider sx={{ my: 0.5 }} />}
                                    </>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>{t('common.cancel')}</Button>
                <Button
                    variant='contained'
                    loading={saving}
                    disabled={generating || !slides || slides.length === 0}
                    onClick={async () => {
                        setSaving(true);
                        for (const components of slides) {
                            await onSave?.(components);
                        }
                        setSaving(false);
                    }}
                >
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
